import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthProvider';
import { DEMO_PROFILES, isDemoAuthEnabled, mintTestToken } from './demoProfiles';

type LoginProps = {
  onSuccess?: () => void;
};

export function Login({ onSuccess }: LoginProps) {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleDemoProfileClick(profileId: string, scopes: readonly string[]) {
    login(mintTestToken({ scopes, sub: `demo-${profileId}` }));
    onSuccess?.();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = token.trim();
    if (!trimmed) {
      setError(t('login.missingToken'));
      return;
    }
    try {
      login(trimmed);
      onSuccess?.();
    } catch {
      setError(t('login.invalidToken'));
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
      <div className="w-full max-w-md space-y-6 rounded-lg border border-border bg-card p-8 shadow-sm">
        <div className="space-y-1 text-center">
          <h1 className="text-xl font-semibold">{t('login.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('login.subtitle')}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label
              htmlFor="jwt-token"
              className="block text-sm font-medium text-foreground"
            >
              {t('login.tokenLabel')}
            </label>
            <textarea
              id="jwt-token"
              value={token}
              onChange={(e) => {
                setToken(e.target.value);
                setError(null);
              }}
              placeholder={t('login.tokenPlaceholder')}
              rows={4}
              className="w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 font-mono text-xs placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <button
            type="submit"
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {t('login.continue')}
          </button>
        </form>
        {isDemoAuthEnabled() && (
          <div className="space-y-3 border-t border-border pt-4">
            <div className="space-y-1 text-center">
              <h2 className="text-sm font-semibold">{t('login.demoProfilesHeading')}</h2>
              <p className="text-xs text-muted-foreground">{t('login.demoProfilesSubtitle')}</p>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {DEMO_PROFILES.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => handleDemoProfileClick(profile.id, profile.scopes)}
                  className="rounded-md border border-input bg-transparent px-3 py-2 text-left text-xs hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <span className="block font-medium text-foreground">{t(profile.labelKey)}</span>
                  <span className="block text-muted-foreground">{t(profile.descriptionKey)}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
